import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import {
  getEngineStatus,
  generateWindowNow,
  updateEngineConfig,
} from "@/lib/content-engine.functions";

export const Route = createFileRoute("/_authenticated/admin/content-engine")({
  component: ContentEngineAdmin,
});

type StatusResp = Awaited<ReturnType<typeof getEngineStatus>>;

function ContentEngineAdmin() {
  const fetchStatus = useServerFn(getEngineStatus);
  const runNow = useServerFn(generateWindowNow);
  const saveCfg = useServerFn(updateEngineConfig);

  const [status, setStatus] = useState<StatusResp | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    try {
      const s = await fetchStatus();
      setStatus(s);
    } catch (e) {
      setMessage((e as Error).message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const cfg = status?.config;

  const onRun = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const r = await runNow({ data: {} });
      setMessage(
        `Generated ${r.generated}, skipped ${r.skipped}${r.errors.length ? `, errors: ${r.errors.join("; ")}` : ""}`,
      );
      await load();
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onSave = async (patch: Record<string, unknown>) => {
    setBusy(true);
    setMessage(null);
    try {
      await saveCfg({ data: patch as any });
      await load();
      setMessage("Saved.");
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-8 px-4 py-10 text-foreground">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">Content Engine</h1>
        <p className="text-sm text-muted-foreground">
          Automated daily activations — Rising, Midday, Evening, Night Seal. AI-drafts a rolling
          window, publishes at slot time, and fans out via push, email, and in-app.
        </p>
      </header>

      {message && (
        <div className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm">
          {message}
        </div>
      )}

      {cfg && (
        <section className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium">Configuration</h2>
              <p className="text-xs text-muted-foreground">
                Last tick: {cfg.last_tick_at ?? "—"} · Last generation: {cfg.last_generation_at ?? "—"}
              </p>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => onSave({ enabled: !cfg.enabled })}
              className={`rounded-md border px-3 py-1.5 text-sm ${cfg.enabled ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-500" : "bg-muted text-muted-foreground"}`}
            >
              {cfg.enabled ? "Engine: ON" : "Engine: OFF"}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {(
              [
                ["morning_local_time", "Rising"],
                ["midday_local_time", "Midday"],
                ["evening_local_time", "Evening"],
                ["night_seal_local_time", "Night Seal"],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="block text-sm">
                <span className="mb-1 block text-muted-foreground">{label}</span>
                <input
                  type="time"
                  defaultValue={(cfg[key] as string).slice(0, 5)}
                  onBlur={(e) => onSave({ [key]: `${e.target.value}:00` })}
                  className="w-full rounded-md border border-border bg-background px-2 py-1"
                />
              </label>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Timezone</span>
              <input
                defaultValue={cfg.timezone}
                onBlur={(e) => e.target.value !== cfg.timezone && onSave({ timezone: e.target.value })}
                className="w-full rounded-md border border-border bg-background px-2 py-1"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Window (days)</span>
              <input
                type="number"
                min={1}
                max={30}
                defaultValue={cfg.window_days}
                onBlur={(e) =>
                  Number(e.target.value) !== cfg.window_days &&
                  onSave({ window_days: Number(e.target.value) })
                }
                className="w-full rounded-md border border-border bg-background px-2 py-1"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">AI model</span>
              <input
                defaultValue={cfg.ai_model}
                onBlur={(e) => e.target.value !== cfg.ai_model && onSave({ ai_model: e.target.value })}
                className="w-full rounded-md border border-border bg-background px-2 py-1"
              />
            </label>
          </div>

          <div>
            <button
              type="button"
              disabled={busy}
              onClick={onRun}
              className="rounded-md bg-amber-500 px-4 py-2 text-sm font-medium text-black hover:bg-amber-400 disabled:opacity-50"
            >
              {busy ? "Working…" : "Generate window now"}
            </button>
          </div>
        </section>
      )}

      {status?.grid && (
        <section className="rounded-xl border border-border bg-card p-5 shadow-sm">
          <h2 className="mb-3 text-lg font-medium">Rolling window</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-2 pr-4">Day</th>
                  <th className="py-2 pr-4">Rising</th>
                  <th className="py-2 pr-4">Midday</th>
                  <th className="py-2 pr-4">Evening</th>
                  <th className="py-2">Night Seal</th>
                </tr>
              </thead>
              <tbody>
                {status.grid.map((row) => (
                  <tr key={row.day} className="border-t border-border/60">
                    <td className="py-2 pr-4 font-mono text-xs">{row.day}</td>
                    {row.slots.map((s) => (
                      <td key={s.slot} className="py-2 pr-4">
                        <span
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${s.filled ? "bg-emerald-500/20 text-emerald-500" : "bg-red-500/10 text-red-500"}`}
                          title={s.publishAt}
                        >
                          {s.filled ? "✓" : "·"}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
